import React, { Fragment, useCallback, useRef, useEffect, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { PlaybookTransformItem, PlaybookMapping, PlaybookValueTransform, PlaybookEventConfig } from '@/types/playbook';
import { Parameter, EventSchema, DataType } from '@/types/schema';
import PropertyPathAutocomplete from '@components/common/PropertyPathAutocomplete';
import ReactDOM from 'react-dom';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { getTypeColorClass } from '@utils/schemaUtils'; // Import getTypeColorClass
import { usePortalRoot } from '@contexts/PortalRootContext';
import { useTooltipPosition } from '@hooks/useTooltipPosition'; // Import useTooltipPosition
import {
  TypeIconString,
  TypeIconNumber,
  TypeIconBoolean,
  TypeIconObject,
  TypeIconArray,
  TypeIconNull,
} from '@components/common/DataTypeIcons'; // Import DataTypeIcons

import { UnifiedSchema } from '@/types/schema'; // Import UnifiedSchema
import { getTransformConfig } from '@/config/playbookTransforms';

// Define DataTypeIcon component locally for use in this file
const DataTypeIcon: React.FC<{ type: DataType; className?: string }> = ({ type, className = "h-4 w-4" }) => {
  switch (type) {
    case 'string': return <TypeIconString className={className} />;
    case 'number': return <TypeIconNumber className={className} />;
    case 'boolean': return <TypeIconBoolean className={className} />;
    case 'object': return <TypeIconObject className={className} />;
    case 'array': return <TypeIconArray className={className} />;
    case 'null': return <TypeIconNull className={className} />;
    default: return null;
  }
};

interface MappingEditorProps {
  transform: PlaybookTransformItem;
  onUpdate: (updates: Partial<PlaybookTransformItem>) => void;
  availableProperties?: Parameter[];
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[]; // New prop
  outputSchema?: UnifiedSchema | null;
  onCloseEditor: () => void;
  usedInputProperties?: string[];
  usedOutputProperties?: string[];
}

const MappingEditor: React.FC<MappingEditorProps> = ({
  transform,
  onUpdate,
  availableProperties = [],
  allEvents,
  uiEventConfigs, // Destructure the new prop
  outputSchema = null,
  onCloseEditor,
  usedInputProperties = [],
  usedOutputProperties = [],
}) => {
  const { portalRoot } = usePortalRoot();
  const content = transform.content as PlaybookMapping;

  const editorRef = useRef<HTMLDivElement>(null); // Ref for the editor container
  const inputKeyRef = useRef<HTMLInputElement>(null); // Ref for the input key field
  const outputKeyRef = useRef<HTMLInputElement>(null); // Ref for the output key field
  const listboxButtonRef = useRef<HTMLButtonElement>(null); // Ref for the ListboxButton
  const listboxOptionsPortalRef = useRef<HTMLDivElement>(null); // New ref for the ListboxOptionsPortal
  const [outputKeyError, setOutputKeyError] = useState<string | null>(null);

  const isGlobalTransform = transform.appliesToScope === 'global';
  const isEventSpecificAndAssignedToAll = 
    transform.appliesToScope === 'eventSpecific' && 
    (transform.eventIds || []).length > 0 && 
    (transform.eventIds || []).length === allEvents.length;

  const updateContent = (updates: Partial<PlaybookMapping>) => {
    onUpdate({
      content: { ...content, ...updates }
    });
  };

  // Helper to detect data type from available properties
  const detectDataType = (propertyPath: string, properties: Parameter[]): DataType | 'any' => {
    const property = properties.find(p => p.fullPath === propertyPath || p.path === propertyPath);
    if (property && property.dataTypes && property.dataTypes.length > 0) {
      return property.dataTypes[0];
    }
    return 'any'; // Default fallback to 'any'
  };

  useEffect(() => {
    if (!content.transforms || content.transforms.length === 0) {
      updateContent({
        transforms: [{ type: 'DirectAssign' }]
      });
    }
  }, [content.transforms, updateContent]);

  const updateValueTransform = (index: number, updates: Partial<PlaybookValueTransform>) => {
    const updatedTransforms = [...(content.transforms || [])];
    const newTransform = { ...updatedTransforms[index], ...updates };
    updatedTransforms[index] = newTransform;
    updateContent({ transforms: updatedTransforms });

    // Perform validation after updating the transform
    if (index === 0 && newTransform.type) { // Only validate for the first (and only) transform
      const selectedTransformType = valueTransformTypes.find(t => t.value === newTransform.type);
      const expectedDataType = selectedTransformType?.outputDataType;

      if (content.outputKey && expectedDataType && expectedDataType !== 'any') {
        const actualOutputKeyDataType = detectDataType(content.outputKey, outputSchema?.parameters || []);
        if (expectedDataType !== actualOutputKeyDataType) {
          setOutputKeyError(`Data type mismatch: Expected '${expectedDataType}', but output key is '${actualOutputKeyDataType}'.`);
        } else {
          setOutputKeyError(null);
        }
      } else {
        setOutputKeyError(null);
      }
    }
  };

  const valueTransformTypes = [
    { value: 'DirectAssign', label: 'Direct Assign', fields: [], outputDataType: 'any' },
    { value: 'JoinValues', label: 'Join Values', fields: ['separator', 'fields'], outputDataType: 'any' },
    { value: 'LowercaseString', label: 'Lowercase', fields: [], outputDataType: 'any' },
    { value: 'UppercaseString', label: 'Uppercase', fields: [], outputDataType: 'any' },
    { value: 'TrimString', label: 'Trim', fields: [], outputDataType: 'any' },
    { value: 'ReplaceString', label: 'Replace String', fields: ['find', 'replace'], outputDataType: 'any' },
    { value: 'SplitString', label: 'Split String', fields: ['separator'], outputDataType: 'array' },
    { value: 'Substring', label: 'Substring', fields: ['start', 'length'], outputDataType: 'any' },
    { value: 'ToBoolean', label: 'To Boolean', fields: [], outputDataType: 'boolean' },
    { value: 'ToFloat', label: 'To Float', fields: [], outputDataType: 'float' },
    { value: 'ToString', label: 'To String', fields: [], outputDataType: 'any' },
    { value: 'ToRoundedInteger', label: 'To Rounded Integer', fields: [], outputDataType: 'integer' },
    { value: 'ToTruncatedInteger', label: 'To Truncated Integer', fields: [], outputDataType: 'integer' },
    { value: 'TruncateFloat', label: 'Truncate Float', fields: [], outputDataType: 'float' },
  ];

  const handleEventSelectChange = useCallback((selectedEventIds: string[]) => {
    onUpdate({ 
      eventIds: selectedEventIds,
      // Preserve existing content and type information
      content: transform.content,
      inputType: transform.inputType,
      outputType: transform.outputType
    });
  }, [onUpdate, transform.content, transform.inputType, transform.outputType]);

  const currentEventAssociations = uiEventConfigs.filter(event =>
    (transform.eventIds || []).includes(event.id)
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseEditor();
    }
  }, [onCloseEditor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the editor and also outside the ListboxOptionsPortal
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node) &&
        listboxOptionsPortalRef.current &&
        !listboxOptionsPortalRef.current.contains(event.target as Node)
      ) {
        onCloseEditor();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCloseEditor]);

  // Focus on the first empty field when component mounts
  useEffect(() => {
    const focusFirstEmptyField = () => {
      if (!content.inputKey && inputKeyRef.current) {
        inputKeyRef.current.focus();
      } else if (!content.outputKey && outputKeyRef.current) {
        outputKeyRef.current.focus();
      }
    };

    // Use a small delay to ensure the component is fully rendered
    const timeoutId = setTimeout(focusFirstEmptyField, 100);
    
    return () => clearTimeout(timeoutId);
  }, []); // Only run on mount

  // Initial validation when component mounts or transform/outputKey changes
  useEffect(() => {
    const currentTransform = content.transforms?.[0];
    if (currentTransform?.type && content.outputKey) {
      const selectedTransformType = valueTransformTypes.find(t => t.value === currentTransform.type);
      const expectedDataType = selectedTransformType?.outputDataType;
      if (expectedDataType && expectedDataType !== 'any') {
        const actualOutputKeyDataType = detectDataType(content.outputKey, outputSchema?.parameters || []);
        if (expectedDataType !== actualOutputKeyDataType) {
          setOutputKeyError(`Data type mismatch: Expected '${expectedDataType}', but output key is '${actualOutputKeyDataType}'.`);
        } else {
          setOutputKeyError(null);
        }
      } else {
        setOutputKeyError(null);
      }
    } else {
      setOutputKeyError(null);
    }
  }, [content.transforms, content.outputKey, outputSchema?.parameters, detectDataType, valueTransformTypes]);

  const effectiveOutputDataType = useCallback((): DataType | 'any' => { // Change return type to DataType | 'any'
    const currentTransforms = content.transforms || [];
    if (currentTransforms.length > 0) {
      const selectedTransformType = valueTransformTypes.find(
        (t) => t.value === currentTransforms[0].type
      );
      if (selectedTransformType?.outputDataType) {
        return selectedTransformType.outputDataType as DataType | 'any'; // Explicit cast
      }
      if (content.inputKey) {
        return detectDataType(content.inputKey, availableProperties) as DataType | 'any'; // Explicit cast
      }
    }
    if (content.inputKey) {
      return detectDataType(content.inputKey, availableProperties) as DataType | 'any'; // Explicit cast
    }
    return 'any' as DataType | 'any'; // Explicit cast
  }, [content.transforms, content.inputKey, availableProperties, detectDataType]);

  const renderValueTransformFields = (valueTransform: PlaybookValueTransform, index: number) => {
    const transformType = valueTransformTypes.find(t => t.value === valueTransform.type);
    if (!transformType || transformType.fields.length === 0) return null; // Only render if there are fields

    return (
      <div className="flex items-end gap-2 flex-wrap"> {/* Changed to flex container for one line */}
        {transformType.fields.includes('separator') && (
          <div className="flex-1 min-w-[120px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Separator
            </label>
            <input
              type="text"
              value={valueTransform.separator || ''}
              onChange={(e) => updateValueTransform(index, { separator: e.target.value })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              placeholder="e.g., ','"
            />
          </div>
        )}

        {transformType.fields.includes('fields') && (
          <div className="flex-1 min-w-[180px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fields (comma-separated)
            </label>
            <input
              type="text"
              value={valueTransform.fields?.join(', ') || ''}
              onChange={(e) => updateValueTransform(index, { 
                fields: e.target.value.split(',').map(f => f.trim()).filter(f => f) 
              })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              placeholder="field1, field2, field3"
            />
          </div>
        )}

        {transformType.fields.includes('find') && (
          <div className="flex-1 min-w-[120px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Find
            </label>
            <input
              type="text"
              value={valueTransform.find || ''}
              onChange={(e) => updateValueTransform(index, { find: e.target.value })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              placeholder="Text to find"
            />
          </div>
        )}

        {transformType.fields.includes('replace') && (
          <div className="flex-1 min-w-[120px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Replace
            </label>
            <input
              type="text"
              value={valueTransform.replace || ''}
              onChange={(e) => updateValueTransform(index, { replace: e.target.value })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              placeholder="Replacement text"
            />
          </div>
        )}

        {transformType.fields.includes('start') && (
          <div className="flex-1 min-w-[100px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Position
            </label>
            <input
              type="number"
              value={valueTransform.start || 0}
              onChange={(e) => updateValueTransform(index, { start: parseInt(e.target.value) || 0 })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              min="0"
            />
          </div>
        )}

        {transformType.fields.includes('length') && (
          <div className="flex-1 min-w-[100px]"> {/* Added flex-1 and min-width */}
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Length
            </label>
            <input
              type="number"
              value={valueTransform.length || 1}
              onChange={(e) => updateValueTransform(index, { length: parseInt(e.target.value) || 1 })}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              min="1"
            />
          </div>
        )}
      </div>
    );
  };

  const mappingTransformConfig = getTransformConfig('mapping');
  const MappingIcon = mappingTransformConfig?.icon;

  return (
    <div ref={editorRef} className="flex flex-col gap-3 py-4 px-4" onKeyDown={handleKeyDown}>
      {/* Input Key and Output Key on one row */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <PropertyPathAutocomplete
            ref={inputKeyRef}
            label="Input Key"
            value={content.inputKey || ''}
            onSelect={(fullPath) => {
              const inputType = detectDataType(fullPath, availableProperties);
              onUpdate({
                content: { ...content, inputKey: fullPath }, // Update content.inputKey
                inputPath: fullPath, // Update transform.inputPath
                inputType: inputType // Use actual input data type
              });
            }}
            availableProperties={availableProperties}
            usedProperties={usedInputProperties.filter(path => path !== content.inputKey)}
            expectedDataType="any"
            placeholder="e.g., user.email"
          />
        </div>
        {MappingIcon && (
          <MappingIcon size={20} className={clsx("mt-6", mappingTransformConfig?.colorClass.replace('bg-', 'text-'))} />
        )}
        <div className="flex-1">
          <PropertyPathAutocomplete
            ref={outputKeyRef}
            label="Output Key"
            value={content.outputKey || ''}
            onSelect={(fullPath) => {
              const outputProperties = outputSchema?.parameters || [];
              const outputType = detectDataType(fullPath, outputProperties);

              onUpdate({
                content: { ...content, outputKey: fullPath }, // Update content.outputKey
                outputPath: fullPath, // Update transform.outputPath
                outputType: outputType // Use actual output data type
              });

              // Re-validate after output key changes
              const currentTransform = content.transforms?.[0];
              if (currentTransform?.type) {
                const selectedTransformType = valueTransformTypes.find(t => t.value === currentTransform.type);
                const expectedDataType = selectedTransformType?.outputDataType;
                if (expectedDataType && expectedDataType !== 'any') {
                  const actualOutputKeyDataType = detectDataType(fullPath, outputProperties);
                  if (expectedDataType !== actualOutputKeyDataType) {
                    setOutputKeyError(`Data type mismatch: Expected '${expectedDataType}', but output key is '${actualOutputKeyDataType}'.`);
                  } else {
                    setOutputKeyError(null);
                  }
                } else {
                  setOutputKeyError(null);
                }
              }
            }}
            availableProperties={outputSchema?.parameters || []}
            usedProperties={usedOutputProperties.filter(path => path !== content.outputKey)}
            expectedDataType={effectiveOutputDataType()}
            placeholder="e.g., contact.email_address"
            className={clsx(outputKeyError && 'border-red-500 ring-red-500')}
          />
          {outputKeyError && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{outputKeyError}</p>
          )}
        </div>
      </div>

      {/* Auto-Map Button */}
      {/* <div className="flex justify-center">
        <button className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center">
          <Wand2 className="h-4 w-4 mr-2" />
          Auto-Map (AI)
        </button>
      </div> */}

      {/* Value Transforms Section */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Value Transform
        </label>
        {content.transforms && content.transforms.length > 0 && (
          <div className="flex flex-col gap-3"> {/* Changed from space-y-3 to flex flex-col gap-3 */}
            <div className="flex items-center gap-2 mb-3"> {/* Removed border, padding, and rounded-lg */}
              <select
                value={content.transforms[0].type}
                onChange={(e) => updateValueTransform(0, { type: e.target.value as any })}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
              >
                {valueTransformTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            {renderValueTransformFields(content.transforms[0], 0)}
          </div>
        )}
      </div>


      {/* Live Preview */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Preview
        </h5>
        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
          {content.inputKey && content.outputKey ? (
            <div className="flex items-center gap-1">
              <DataTypeIcon type={detectDataType(content.inputKey, availableProperties) as DataType} />
              <span className={clsx("font-semibold", getTypeColorClass(detectDataType(content.inputKey, availableProperties) as DataType))}>
                {content.inputKey}
              </span>
              <span className="text-gray-500"> = </span>
              <DataTypeIcon type={detectDataType(content.outputKey, outputSchema?.parameters || []) as DataType} />
              <span className={clsx("font-semibold", getTypeColorClass(detectDataType(content.outputKey, outputSchema?.parameters || []) as DataType))}>
                {content.outputKey}
              </span>
              {content.transforms && content.transforms.length > 0 && (
                <span className="text-purple-600 dark:text-purple-400">
                  {' '}→ {content.transforms.length} transform{content.transforms.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">Enter input and output keys to see preview</span>
          )}
        </div>
      </div>

      {/* Assigned Events */}
      <div>
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          Assigned Events
        </label>
        <div className="flex flex-row gap-2 items-end w-full">
          <Listbox
            value={currentEventAssociations}
            onChange={(selectedEvents: EventSchema[]) => handleEventSelectChange(selectedEvents.map(event => event.id))}
            multiple
            disabled={isGlobalTransform}
          >
            {({ open }) => (
              <div className="relative flex-1">
                <ListboxButton
                  ref={listboxButtonRef}
                  className={clsx(
                    "relative w-full cursor-default rounded border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm",
                    "border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500/30 dark:focus:ring-blue-500/20",
                    "bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100",
                    isGlobalTransform && "bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  )}
                >
                  <div className="flex flex-wrap gap-1 pr-8">
                    {isGlobalTransform ? (
                      <span className="text-gray-500 dark:text-gray-400">Global Transform (applies to all events)</span>
                    ) : currentEventAssociations.length > 0 ? (
                      currentEventAssociations.map(event => (
                        <span
                          key={event.id}
                          className="inline-flex items-center rounded-full bg-blue-200 px-2.5 py-0.5 text-xs font-medium text-blue-900 dark:bg-blue-700 dark:text-blue-100"
                        >
                          {event.name}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                handleEventSelectChange(transform.eventIds!.filter(id => id !== event.id));
                              }
                            }}
                            className="ml-1 -mr-0.5 h-4 w-4 flex-shrink-0 rounded-full hover:bg-blue-300 dark:hover:bg-blue-600 inline-flex items-center justify-center text-blue-900 dark:text-blue-100"
                          >
                            <span className="sr-only">Remove {event.name}</span>
                            <svg className="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Select events...</span>
                    )}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronDown
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </ListboxButton>

                {portalRoot && (
                  <ListboxOptionsPortal
                    triggerRef={listboxButtonRef}
                    portalRoot={portalRoot}
                    isOpen={open && !isGlobalTransform}
                    forwardedRef={listboxOptionsPortalRef} // Pass the new ref
                  >
                    {uiEventConfigs.map((event) => (
                      <ListboxOption
                        key={event.id}
                        className={({ active }) =>
                          clsx(
                            active ? 'text-white bg-blue-600' : 'text-gray-900 dark:text-gray-100',
                            'relative cursor-pointer select-none py-2 pl-10 pr-4' // Changed cursor-default to cursor-pointer
                          )
                        }
                        value={event}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={clsx(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                              {event.name}
                            </span>
                            {selected ? (
                              <span
                                className={clsx(
                                  active ? 'text-white' : 'text-blue-600',
                                  'absolute inset-y-0 left-0 flex items-center pl-3'
                                )}
                              >
                                <Check className="h-5 w-5" aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptionsPortal>
                )}
              </div>
            )}
          </Listbox>
          {isGlobalTransform ? (
            <button
              type="button"
              onClick={() => {
                const updateData = { 
                  appliesToScope: 'eventSpecific' as const, 
                  eventIds: uiEventConfigs.map(event => event.id),
                  // Preserve existing type information
                  inputType: transform.inputType,
                  outputType: transform.outputType
                };
                onUpdate(updateData);
              }}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              )}
            >
              Make Event-specific
            </button>
          ) : isEventSpecificAndAssignedToAll ? (
            <button
              type="button"
              onClick={() => {
                const updateData = { 
                  appliesToScope: 'global' as const, 
                  eventIds: undefined,
                  // Preserve existing type information
                  inputType: transform.inputType,
                  outputType: transform.outputType
                };
                onUpdate(updateData);
              }}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-blue-600 text-white hover:bg-blue-700"
              )}
            >
              Promote to Global
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                handleEventSelectChange(uiEventConfigs.map(event => event.id));
              }}
              disabled={(transform.eventIds || []).length === uiEventConfigs.length}
              className={clsx(
                "flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors",
                "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600",
                (transform.eventIds || []).length === uiEventConfigs.length ? 'opacity-60 cursor-not-allowed' : ''
              )}
            >
              Assign All
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface ListboxOptionsPortalProps {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  portalRoot: HTMLElement;
  isOpen: boolean;
  // Add a ref prop to allow the parent to access the portal's root element
  forwardedRef: React.RefObject<HTMLDivElement>; 
}

const ListboxOptionsPortal: React.FC<ListboxOptionsPortalProps> = ({
  children,
  triggerRef,
  portalRoot,
  isOpen,
  forwardedRef, // Destructure the new prop
}) => {
  const { tooltipStyle, tooltipRef } = useTooltipPosition(triggerRef, {
    side: 'bottom',
    align: 'start', // Reverted align from 'center' to 'start'
    sideOffset: 2,
    portalRoot,
    isOpen,
  });

  // Assign the tooltipRef to the forwardedRef
  useEffect(() => {
    if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<HTMLDivElement | null>).current = tooltipRef.current;
    }
  }, [forwardedRef, tooltipRef]);

  return ReactDOM.createPortal(
    <div
      ref={tooltipRef} // Keep tooltipRef here for useTooltipPosition
      style={{ ...tooltipStyle, width: tooltipStyle.width || 'auto' }}
      className="pointer-events-auto z-50"
    >
      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <ListboxOptions
          static
          className="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm
            dark:bg-gray-800 dark:border dark:border-gray-700"
        >
          {children}
        </ListboxOptions>
      </Transition>
    </div>,
    portalRoot
  );
};

export default MappingEditor;
